/**
 * Extract a user-readable error message from a FastAPI error payload.
 *
 * Handles every shape we throw from the backend:
 *   - { detail: "string" }              -> the string
 *   - { detail: [{msg, ...}, ...] }     -> Pydantic 422 errors, joined
 *   - { detail: { message, ... } }      -> our HTTPException(detail=dict),
 *                                          e.g. budget_guard blockers
 *   - { detail: <other object> }        -> JSON.stringify fallback
 *
 * Pass either:
 *   - the response body itself (e.g. `await res.json()`),
 *   - or an axios-style error (we look at err.response.data),
 *   - or even the raw error/string — the helper handles all of them.
 *
 * Always returns a string suitable for direct rendering in <span>{msg}</span>.
 */
export function extractErrorMessage(input: unknown, fallback = 'Something went wrong'): string {
  if (!input) return fallback;
  if (typeof input === 'string') return input || fallback;

  // Axios error: peek at response.data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyInput = input as any;
  if (anyInput?.response?.data) {
    return extractErrorMessage(anyInput.response.data, fallback);
  }

  // Direct response body
  const detail = anyInput?.detail;
  if (detail === undefined || detail === null) {
    // No detail field — maybe the body itself is the message-bearing object
    if (typeof anyInput?.message === 'string') return anyInput.message;
    return fallback;
  }
  if (typeof detail === 'string') return detail || fallback;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((e: unknown) => {
        if (typeof e === 'string') return e;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ee = e as any;
        return ee?.msg || ee?.message || JSON.stringify(e);
      })
      .filter(Boolean);
    return parts.join('; ') || fallback;
  }
  if (typeof detail === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dd = detail as any;
    if (typeof dd.message === 'string') return dd.message;
    if (typeof dd.msg === 'string') return dd.msg;
    try {
      return JSON.stringify(dd);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

/**
 * If the backend sent a structured blocker object (e.g. budget_guard
 * balance_insufficient), return it for UI that wants to display the
 * breakdown (required, available, currency, ...). Returns null for
 * plain string/array errors.
 */
export function extractErrorDetailObject(input: unknown): Record<string, unknown> | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyInput = input as any;
  const detail = anyInput?.response?.data?.detail ?? anyInput?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as Record<string, unknown>;
  }
  return null;
}
